<template>
  <b-modal
    v-model="visible"
    modal-class="lz-activity-modal lizard-resource-modal"
    content-class="lz-modal-content"
    size="md"
    centered
    scrollable
    :busy="saving"
    @hidden="Reset">
    <template #modal-header="{ close }">
      <div>
        <h5 class="lizard-rm-title">{{ modalTitle }}</h5>
        <p class="lizard-rm-subtitle">
          {{
            isEdit
              ? 'Update the details of this resource.'
              : 'New resources start hidden until you make them visible.'
          }}
        </p>
      </div>
      <b-button-close @click="close()"></b-button-close>
    </template>

    <b-form @submit.prevent="Save">
      <label class="lizard-rm-label">Title</label>
      <b-form-input
        v-model="form.title"
        class="lizard-rm-input"
        :state="titleState"
        placeholder="e.g. NHS Digital"
        trim />

      <template v-if="parentOptions.length">
        <label class="lizard-rm-label">Section</label>
        <b-form-select
          v-model="form.parentUid"
          class="lizard-rm-input"
          :state="parentState"
          :options="parentSelectOptions" />
      </template>

      <div class="lizard-rm-divider">Links</div>
      <b-input-group class="lizard-rm-group">
        <b-input-group-prepend is-text>
          <b-icon-box-arrow-in-up-right scale="0.9"></b-icon-box-arrow-in-up-right>
        </b-input-group-prepend>
        <b-form-input
          v-model="form.link"
          class="lizard-rm-input"
          placeholder="Open link, https://..."
          trim />
      </b-input-group>
      <b-input-group class="lizard-rm-group">
        <b-input-group-prepend is-text>
          <b-icon-camera-video scale="0.9"></b-icon-camera-video>
        </b-input-group-prepend>
        <b-form-input
          v-model="form.videoLink"
          class="lizard-rm-input"
          placeholder="Videos link, https://..."
          trim />
      </b-input-group>
      <b-input-group class="lizard-rm-group">
        <b-input-group-prepend is-text>
          <b-icon-files scale="0.9"></b-icon-files>
        </b-input-group-prepend>
        <b-form-input
          v-model="form.filesLink"
          class="lizard-rm-input"
          placeholder="Files link, https://..."
          trim />
      </b-input-group>

      <div class="lizard-rm-divider">Display</div>
      <div class="row">
        <div class="col-6" v-if="showRowField">
          <label class="lizard-rm-label">Row</label>
          <b-form-input
            v-model.number="form.row"
            class="lizard-rm-input"
            type="number"
            min="1" />
        </div>
        <div class="col-6">
          <label class="lizard-rm-label">Sort Order</label>
          <b-form-input
            v-model.number="form.sortOrder"
            class="lizard-rm-input"
            type="number"
            min="0" />
        </div>
      </div>
      <div class="lizard-rm-visible">
        <div>
          <div class="lizard-rm-visible-title">Visibility</div>
          <div class="lizard-rm-hint">Hidden resources only appear in edit mode.</div>
        </div>
        <button
          type="button"
          class="lizard-rm-toggle"
          :class="{ on: form.show }"
          @click="form.show = !form.show">
          <span class="lizard-rm-toggle-track">
            <span class="lizard-rm-toggle-knob"></span>
          </span>
          <span class="lizard-rm-toggle-label">{{ form.show ? 'Visible' : 'Hidden' }}</span>
        </button>
      </div>
    </b-form>

    <template #modal-footer>
      <b-button
        size="sm"
        variant="outline-secondary"
        class="lz-close-btn"
        :disabled="saving"
        @click="visible = false">
        Cancel
      </b-button>
      <b-button
        size="sm"
        variant="primary"
        class="lz-close-btn lizard-rm-save"
        :disabled="saving"
        @click="Save">
        <b-spinner v-if="saving" small></b-spinner>
        <span v-else>{{ isEdit ? 'Save Changes' : 'Add Resource' }}</span>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import Services from '../../services/main.service.vue'

export default {
  name: 'HomeResourceFormModal',
  props: {
    tab: {
      type: String,
      required: true
    },
    parentOptions: {
      type: Array,
      required: false,
      default: () => []
    },
    showRowField: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      visible: false,
      saving: false,
      submitted: false,
      resource: null,
      form: this.EmptyForm()
    }
  },
  computed: {
    isEdit() {
      return Boolean(this.resource?.uid)
    },
    tabLabel() {
      const labels = {
        'useful-links': 'Useful Links',
        training: 'Training Modules',
        forms: 'Documents and Forms'
      }
      return labels[this.tab] ?? this.tab
    },
    modalTitle() {
      if (this.isEdit) {
        return 'Edit Resource'
      }
      return `Add Resource to “${this.tabLabel}”`
    },
    titleState() {
      return this.submitted ? this.form.title.trim().length > 0 : null
    },
    parentState() {
      return this.submitted ? Boolean(this.form.parentUid) : null
    },
    parentSelectOptions() {
      return this.parentOptions
        .filter((opt) => opt.value !== this.resource?.uid)
        .map((opt) => ({ value: opt.value, text: opt.text }))
    }
  },
  methods: {
    EmptyForm() {
      return {
        title: '',
        parentUid: null,
        link: '',
        videoLink: '',
        filesLink: '',
        // new resources start hidden so admins can review before publishing
        show: false,
        sortOrder: 0,
        row: 1
      }
    },
    Open(resource = null, defaults = {}) {
      this.resource = resource
      this.form = {
        ...this.EmptyForm(),
        ...defaults,
        ...(resource
          ? {
              title: resource.title ?? '',
              parentUid: resource.parentUid ?? null,
              link: resource.link ?? '',
              videoLink: resource.videoLink ?? '',
              filesLink: resource.filesLink ?? '',
              show: resource.show !== false,
              sortOrder: resource.sortOrder ?? 0,
              row: resource.meta?.row ?? 1
            }
          : {})
      }
      this.submitted = false
      this.visible = true
    },
    Reset() {
      this.resource = null
      this.form = this.EmptyForm()
      this.submitted = false
      this.saving = false
    },
    async Save() {
      this.submitted = true
      if (!this.form.title.trim().length) {
        return
      }
      if (this.parentOptions.length && !this.form.parentUid) {
        return
      }
      try {
        this.saving = true
        const payload = {
          title: this.form.title.trim(),
          parentUid: this.form.parentUid,
          link: this.form.link.trim() || null,
          videoLink: this.form.videoLink.trim() || null,
          filesLink: this.form.filesLink.trim() || null,
          show: this.form.show,
          sortOrder: Number(this.form.sortOrder) || 0
        }
        if (this.showRowField) {
          payload.meta = {
            ...(this.resource?.meta ?? {}),
            row: Number(this.form.row) || 1
          }
        } else if (this.resource?.meta && Object.keys(this.resource.meta).length) {
          payload.meta = this.resource.meta
        }

        const { status, data } = this.isEdit
          ? await Services.Home.UpdateHomeResource(this.resource.uid, payload)
          : await Services.Home.CreateHomeResource({ tab: this.tab, ...payload })

        if (status === 200 && data?.success !== false) {
          this.visible = false
          this.$emit('saved')
        } else {
          this.ShowError(data)
        }
      } catch (error) {
        this.ShowError(error?.response?.data ?? error)
      } finally {
        this.saving = false
      }
    },
    ShowError(data) {
      const validationMessage = data?.error?.validationErrors?.[0]?.message
      const message =
        validationMessage ?? data?.error?.message ?? data?.message ?? 'Failed to save, please try again later.'
      this.$toasted.error(message).goAway(3500)
    }
  }
}
</script>

<style>
/* Shell (radius, header/footer padding) comes from .lz-activity-modal in
   src/styles/logs.css; only the form internals are styled here. */
.lizard-resource-modal .modal-header {
  align-items: flex-start;
}
.lizard-rm-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
}
.lizard-rm-subtitle {
  margin: 2px 0 0;
  font-size: 12.5px;
  color: #64748b;
}
.lizard-resource-modal .modal-body {
  padding: 16px 22px 6px;
}
.lizard-rm-label {
  display: block;
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #999;
}
.lizard-rm-label + .lizard-rm-input,
.lizard-rm-label + .b-form-select {
  margin-bottom: 14px;
}
.lizard-resource-modal .lizard-rm-input,
.lizard-resource-modal select.lizard-rm-input {
  border: 1px solid #dfe3e8;
  border-radius: 8px;
  font-size: 14px;
  height: 38px;
}
.lizard-resource-modal .lizard-rm-input:focus {
  border-color: rgba(0, 115, 190, 0.6);
  box-shadow: 0 0 0 3px rgba(0, 115, 190, 0.08);
}
.lizard-rm-divider {
  margin: 6px 0 10px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #bbb;
}
.lizard-rm-group {
  margin-bottom: 10px;
}
.lizard-rm-group .input-group-text {
  background-color: #f8f9fa;
  border: 1px solid #dfe3e8;
  border-right: none;
  border-radius: 8px 0 0 8px;
  color: #9aa5af;
}
.lizard-rm-group .lizard-rm-input {
  border-radius: 0 8px 8px 0;
}
.lizard-rm-visible {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 4px 0 10px;
  padding: 10px 14px;
  background: #f8f9fa;
  border-radius: 8px;
}
.lizard-rm-visible-title {
  font-size: 14px;
  font-weight: 600;
  color: #555;
}
.lizard-rm-hint {
  font-size: 12px;
  color: #aaa;
}
.lizard-rm-toggle {
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  outline: none;
}
.lizard-rm-toggle-track {
  position: relative;
  display: inline-block;
  width: 38px;
  height: 22px;
  border-radius: 11px;
  background: #ccd4da;
  transition: background 0.15s ease;
  margin-right: 8px;
  flex-shrink: 0;
}
.lizard-rm-toggle-knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  transition: left 0.15s ease;
}
.lizard-rm-toggle.on .lizard-rm-toggle-track {
  background: rgba(0, 115, 190, 0.85);
}
.lizard-rm-toggle.on .lizard-rm-toggle-knob {
  left: 19px;
}
.lizard-rm-toggle-label {
  min-width: 46px;
  text-align: left;
  font-size: 13.5px;
  font-weight: 600;
  color: #999;
  transition: color 0.15s ease;
}
.lizard-rm-toggle.on .lizard-rm-toggle-label {
  color: rgba(0, 115, 190, 0.95);
}
.lizard-rm-save.btn {
  min-width: 120px;
  background-color: #0073be;
  border-color: #0073be;
}
.lizard-rm-save.btn:hover:not(:disabled),
.lizard-rm-save.btn:focus:not(:disabled) {
  background-color: #005e99;
  border-color: #005e99;
}
</style>
